import { observer } from "mobx-react-lite";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Instance } from "mobx-state-tree";
import { mk8sMobx } from "../../../../mst/kinds/mk8s/mk8s";
import { Mk8sDraftMobx } from "../../../../mst/stores/mk8s.draft";
import { Mk8sDraftProviderOblivusMobx } from "../../../../mst/stores/mk8s.draft.provider.oblivus";
import { Mk8sDraftUnmanagedNodePoolModel } from "../../../../mst/stores/mk8s.draft.provider.common";
import { NGInput } from "../../../../newcomponents/input/input";
import { NGButton } from "../../../../newcomponents/button/Button";
import { NGFormElement } from "../../../../newcomponents/ngformelement/ngformelement";
import { NGInputListMst } from "../../../../newcomponents/inputList/inputListMst";
import { NGSelect } from "../../../../newcomponents/select/ngselect";
import { getDryRunErrorText } from "../../getDryRunErrorText";
import { mk8sDryRun } from "../../../../mobxStores/dryRun/mk8s";
import { Mk8sScriptModal } from "../../Mk8sScriptModal";
import { Dropdown, Modal, notification } from "antd";
import { request, resourceLink } from "../../../../services/cpln";
import { Theme } from "../../../../mobxStores/uiData/theme";
import { ChevronDown } from "react-feather";

interface Props {
  mk8s: mk8sMobx;
  mk8sDraft: Mk8sDraftMobx;
  oblivus: Mk8sDraftProviderOblivusMobx;
  nodePool: Instance<typeof Mk8sDraftUnmanagedNodePoolModel>;
  index: number;
}
const ProviderOblivusUnmanagedNodePoolRaw: React.FC<Props> = ({ mk8sDraft, oblivus: o, nodePool: np, index }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [joinScriptIndex, setJoinScriptIndex] = React.useState<number>(-1);
  const [joinIpAddress, setJoinIpAddress] = React.useState<string>("");
  const [joinScript, setJoinScript] = React.useState<string>("");
  const [isJoinScriptLoading, setIsJoinScriptLoading] = React.useState<boolean>(false);

  const pre = `oblivus.unmanagedNodePools[${index}].`;

  const labelsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.oblivus.nodePools[${index}].labels`,
    paths: np.labels.items.map((i) => `spec.provider.oblivus.nodePools[${index}].labels.${i.firstValue}`),
  });
  const taintsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.oblivus.nodePools[${index}].taints`,
    paths: [
      ...np.taints.items.map((_, idx) => `spec.provider.oblivus.nodePools[${index}].taints[${idx}]`),
      ...np.taints.items.map((_, idx) => `spec.provider.oblivus.nodePools[${index}].taints[${idx}].key`),
      ...np.taints.items.map((_, idx) => `spec.provider.oblivus.nodePools[${index}].taints[${idx}].value`),
      ...np.taints.items.map((_, idx) => `spec.provider.oblivus.nodePools[${index}].taints[${idx}].effect`),
    ],
  });

  return (
    <>
      <div>
        <div className="flex justify-between">
          <NGFormElement
            name={`${pre}name`}
            label={np.name.label}
            value={np.name.value}
            onChange={np.name.setValue}
            required
            error={!o.isUnmanagedNodePoolNameValid(np.index) ? "Node pool names must be unique" : ""}
          />
          <div>
            <Dropdown
              trigger={["click"]}
              menu={{
                onClick: async function ({ key }: any) {
                  switch (key) {
                    case "remove":
                      o.removeUnmanagedNodePoolAt(np.index);
                      navigate(`${pathname.split("/-unmanaged-node-pools")[0]}/-unmanaged-node-pools`);
                      break;
                    case "join":
                      if (mk8sDraft.isDirty) {
                        Modal.warning({
                          title: "Unmanaged Pool Join Script",
                          content: "Please save your changes before trying to create the join script.",
                        });
                        return;
                      }
                      setJoinScriptIndex(np.index);
                      break;
                    default:
                      break;
                  }
                },
                items: [
                  { key: "join", label: "Download Join Script" },
                  { key: "remove", label: "Remove", danger: true },
                ],
              }}
            >
              <NGButton
                variant="primary"
                outlined={Theme.theme === "light"}
                size="small"
                renderIcon={(hover, props) => <ChevronDown {...props} />}
              >
                Node Pool Actions
              </NGButton>
            </Dropdown>
          </div>
        </div>
        <NGInputListMst
          data={np.labels}
          className="mb-8"
          label="Labels"
          firstLabel="Key"
          firstInput={(i) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && labelsDryRun.message.includes(`.${i.firstValue}`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          invalid={labelsDryRun.severity === "error"}
          error={labelsDryRun.severity === "error" ? labelsDryRun.message : ""}
          warning={labelsDryRun.severity === "error" ? "" : labelsDryRun.message}
        />
        <NGInputListMst
          data={np.taints}
          className="mb-8"
          label="Taints"
          firstLabel="Key"
          firstInput={(i, index) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && taintsDryRun.message.includes(`[${index}].key`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }
            if (i.secondValue && taintsDryRun.message.includes(`[${index}].value`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          thirdLabel="Effect"
          thirdInput={(i) => {
            let invalid = false;
            if (i.thirdValue && taintsDryRun.message.includes(`[${index}].effect`)) {
              invalid = true;
            }

            return (
              <NGSelect
                invalid={invalid}
                value={i.thirdValue}
                onChange={(value) => i.setThirdValue(value)}
                options={[
                  { label: "NoSchedule", value: "NoSchedule" },
                  { label: "PreferNoSchedule", value: "PreferNoSchedule" },
                  { label: "NoExecute", value: "NoExecute" },
                ]}
              />
            );
          }}
          invalid={taintsDryRun.severity === "error"}
          error={taintsDryRun.severity === "error" ? taintsDryRun.message : ""}
          warning={taintsDryRun.severity === "error" ? "" : taintsDryRun.message}
        />
      </div>
      {!!joinScript || joinScriptIndex === -1 ? null : (
        <Modal
          open={joinScriptIndex !== -1}
          title={"Unmanaged Node Pool Join Script"}
          footer={
            <div className="modal-actions">
              <NGButton
                variant="secondary"
                onClick={() => {
                  setJoinScriptIndex(-1);
                  setJoinIpAddress("");
                  setJoinScript("");
                }}
                disabled={isJoinScriptLoading}
              >
                Cancel
              </NGButton>
              <NGButton
                variant="primary"
                onClick={async () => {
                  try {
                    setIsJoinScriptLoading(true);
                    const {
                      data: { script },
                    } = await request({
                      method: "post",
                      url: resourceLink("mk8s", mk8sDraft._name) + "/-join",
                      body: {
                        nodePoolName: o.unmanagedNodePools.find((np) => np.index === joinScriptIndex)!.name.value,
                        ipAddress: joinIpAddress,
                      },
                    });
                    setIsJoinScriptLoading(false);
                    setJoinIpAddress("");
                    setJoinScript(script);
                  } catch (e) {
                    let errorMessage = e?.response?.data?.message;
                    if (!errorMessage) errorMessage = e.message;
                    notification.warning({ message: "Failed to create join script", description: errorMessage });
                    setIsJoinScriptLoading(false);
                  }
                }}
                disabled={isJoinScriptLoading || !joinIpAddress}
                loading={isJoinScriptLoading}
              >
                OK
              </NGButton>
            </div>
          }
          onCancel={() => {
            setJoinScriptIndex(-1);
            setJoinIpAddress("");
            setJoinScript("");
          }}
        >
          <div className="flex flex-col">
            <NGFormElement
              name="ipAddress"
              label={"IP Address"}
              value={joinIpAddress}
              onChange={(value) => setJoinIpAddress(value)}
            />
          </div>
        </Modal>
      )}
      {!!joinScript ? (
        <Mk8sScriptModal
          clusterName={mk8sDraft._name}
          nodePoolName={o.unmanagedNodePools.find((np) => np.index === joinScriptIndex)!.name.value}
          script={joinScript}
          poolType={"Unmanaged Node Pool"}
          scriptName={"Join Script"}
          extension={"sh"}
          onDone={() => {
            setJoinScriptIndex(-1);
            setJoinIpAddress("");
            setJoinScript("");
          }}
          visible={!!joinScript}
        />
      ) : null}
    </>
  );
};

export const ProviderOblivusUnmanagedNodePool = observer(ProviderOblivusUnmanagedNodePoolRaw);
